import { AnalyticsService } from '@alison/analytics/client';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { MultiScopeSelectControl } from '../../../filters/ScopeRow.js';
import type { TagCategory } from '../data/api.js';
import { CategoryLabel } from './CategoryLabel.js';
import { TagTimeline } from './TagTimeline.js';
import {
  countTags,
  featureOptions,
  filterTagCategories,
  latestSpanEnd,
  type TagFilter,
  tagOptions,
} from './tagTree.js';

const ROW_INDENT = { category: 4, feature: 12, tag: 20 } as const;

interface TagsPanelProps {
  categories: TagCategory[];
  tagCount: number;
  durationSeconds: number | null;
  isVideo: boolean;
}

function selectOptions(names: string[]): { id: string; label: string }[] {
  return names.map((name) => ({ id: name, label: name }));
}

function featureKey(category: string, feature: string): string {
  return `${category}::${feature}`;
}

function expandableKeys(categories: TagCategory[]): string[] {
  const keys: string[] = [];
  for (const category of categories) {
    if (category.features.length === 0) continue;
    keys.push(category.category);
    for (const feature of category.features) {
      if (feature.tags.length === 0) continue;
      keys.push(featureKey(category.category, feature.feature));
    }
  }
  return keys;
}

function TreeRow({
  indent,
  onClick,
  children,
}: {
  indent: number;
  onClick?: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <Box
      component={onClick === undefined ? 'div' : 'button'}
      type={onClick === undefined ? undefined : 'button'}
      onClick={onClick}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        border: 0,
        borderBottom: '1px solid',
        borderColor: theme.palette.border.default,
        bgcolor: 'transparent',
        fontFamily: 'inherit',
        cursor: onClick === undefined ? 'default' : 'pointer',
        textAlign: 'left',
        py: 2,
        pr: 4,
        pl: indent,
      })}
    >
      {children}
    </Box>
  );
}

function Caret({ expanded }: { expanded: boolean }): ReactNode {
  return (
    <KeyboardArrowRightIcon
      sx={{
        fontSize: 18,
        color: 'text.secondary',
        transform: expanded ? 'rotate(90deg)' : 'none',
        flexShrink: 0,
      }}
    />
  );
}

function TagTree({
  categories,
  expandedKeys,
  forceExpanded,
  onToggle,
}: {
  categories: TagCategory[];
  expandedKeys: Set<string>;
  forceExpanded: boolean;
  onToggle: (key: string) => void;
}): ReactNode {
  if (categories.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          No tags match the current filter.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {categories.map((category) => {
        const categoryExpanded = forceExpanded || expandedKeys.has(category.category);
        return (
          <Box key={category.category}>
            <TreeRow indent={ROW_INDENT.category} onClick={() => onToggle(category.category)}>
              <Caret expanded={categoryExpanded} />
              <CategoryLabel category={category.category} />
            </TreeRow>
            {categoryExpanded &&
              category.features.map((feature) => {
                const key = featureKey(category.category, feature.feature);
                const featureExpanded = forceExpanded || expandedKeys.has(key);
                return (
                  <Box key={feature.feature}>
                    <TreeRow indent={ROW_INDENT.feature} onClick={() => onToggle(key)}>
                      <Caret expanded={featureExpanded} />
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary' }}>
                        {feature.feature}
                      </Typography>
                    </TreeRow>
                    {featureExpanded &&
                      feature.tags.map((tag) => (
                        <TreeRow key={tag.tag} indent={ROW_INDENT.tag}>
                          <SellOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                            {tag.tag}
                          </Typography>
                        </TreeRow>
                      ))}
                  </Box>
                );
              })}
          </Box>
        );
      })}
    </Box>
  );
}

export function TagsPanel({
  categories,
  tagCount,
  durationSeconds,
  isVideo,
}: TagsPanelProps): ReactNode {
  const [filter, setFilter] = useState<TagFilter>({ features: [], tags: [] });
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const filteredCategories = filterTagCategories(categories, filter);
  const shownCount = countTags(filteredCategories);
  const filterActive = filter.features.length > 0 || filter.tags.length > 0;

  function toggle(key: string): void {
    setExpandedKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const cutSeconds = durationSeconds ?? latestSpanEnd(categories);
  const timelineDuration = isVideo && cutSeconds > 0 ? cutSeconds : null;

  const allKeys = expandableKeys(filteredCategories);
  const allExpanded = allKeys.length > 0 && allKeys.every((key) => expandedKeys.has(key));
  const toggleAll = (): void => {
    AnalyticsService.track('Creative Detail - Toggle All Tags', { expanded: !allExpanded });
    setExpandedKeys(allExpanded ? new Set() : new Set(allKeys));
  };

  const featureRequired = filter.features.length === 0;
  const tagControl = (
    <MultiScopeSelectControl
      label="Tag"
      placeholderLabel="Any"
      options={selectOptions(tagOptions(categories, filter.features))}
      selected={filter.tags}
      centered
      disabled={featureRequired}
      onChange={(tags) => {
        AnalyticsService.track('Creative Detail - Filter Tags', { filter: 'tag', values: tags });
        setFilter((previous) => ({ ...previous, tags }));
      }}
    />
  );

  return (
    <Stack sx={{ gap: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <MultiScopeSelectControl
          label="Element"
          placeholderLabel="Any"
          options={selectOptions(featureOptions(categories))}
          selected={filter.features}
          centered
          onChange={(features) => {
            AnalyticsService.track('Creative Detail - Filter Tags', {
              filter: 'element',
              values: features,
            });
            const available = tagOptions(categories, features);
            setFilter((previous) => ({
              features,
              tags:
                features.length === 0 ? [] : previous.tags.filter((tag) => available.includes(tag)),
            }));
          }}
        />
        {featureRequired ? (
          <Tooltip
            title="Add the Element filter first"
            placement="top"
            disableInteractive
            slotProps={{
              popper: { modifiers: [{ name: 'offset', options: { offset: [0, -10] } }] },
            }}
          >
            <Box component="span" sx={{ display: 'block' }}>
              {tagControl}
            </Box>
          </Tooltip>
        ) : (
          tagControl
        )}
        {!filterActive && allKeys.length > 0 && (
          <Box
            component="button"
            type="button"
            onClick={toggleAll}
            sx={{
              bgcolor: 'transparent',
              border: 'none',
              fontSize: 12,
              fontWeight: 600,
              color: 'primary.main',
              cursor: 'pointer',
              p: 0,
              transition: (theme) =>
                theme.transitions.create('opacity', {
                  duration: theme.transitions.duration.shortest,
                }),
              '&:hover': { opacity: 0.7 },
            }}
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Box>
        )}
        <Typography sx={{ fontSize: 12, color: 'text.secondary', ml: 'auto' }}>
          {shownCount} of {tagCount} tags shown
        </Typography>
      </Box>
      <TagTree
        categories={filteredCategories}
        expandedKeys={expandedKeys}
        forceExpanded={filterActive}
        onToggle={toggle}
      />
      {timelineDuration !== null && (
        <TagTimeline durationSeconds={timelineDuration} categories={filteredCategories} />
      )}
    </Stack>
  );
}
